import { useEffect, useRef, useState } from "react"
import { gsap } from "gsap"
import { 
  BookOpen, Home, Trophy, Settings, Star, Clock, Download, Heart, LogOut, User, Crown, Users, Plus, Video, BarChart3, Award, UserCheck, Building, TrendingUp,
  Code, Palette, Calculator, Camera, Globe, Monitor, Smartphone, Database, Cloud, Cpu, Briefcase, GraduationCap,
  Music, Gamepad2, Wrench, Lightbulb, Target, PieChart, LineChart, Activity, Zap, Shield, Lock, Key
} from "lucide-react"
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar"
import { Badge } from "./ui/badge"
import { Separator } from "./ui/separator"
import { useAuth } from "../contexts/AuthContext"
import { useNavigate } from "react-router-dom"
import { cursosAPI } from "../services/api"

// Função para criar itens de navegação para usuários normais
const getNormalNavigationItems = (meusCursosCount) => [
  {
    title: "Início",
    url: "/",
    icon: Home,
    badge: null,
  },
  {
    title: "Meus Cursos",
    url: "/meus-cursos",
    icon: BookOpen,
    badge: meusCursosCount > 0 ? meusCursosCount.toString() : null,
  },
  {
    title: "Meus Certificados",
    url: "/meus-certificados",
    icon: Award,
    badge: null,
  },
  {
    title: "Favoritos",
    url: "#",
    icon: Heart,
    badge: null,
  },
  {
    title: "Downloads",
    url: "#",
    icon: Download,
    badge: null,
  },
  {
    title: "Histórico",
    url: "#",
    icon: Clock,
    badge: null,
  },
]

// Itens de navegação para administradores
const adminNavigationItems = [
  {
    title: "Dashboard",
    url: "/admin",
    icon: BarChart3,
    badge: null,
  },
  {
    title: "Estatísticas",
    url: "/admin/estatisticas",
    icon: TrendingUp,
    badge: null,
  },
  {
    title: "Gerenciar Usuários",
    url: "/admin/usuarios",
    icon: Users,
    badge: null,
  },
  {
    title: "Gerenciar Cursos",
    url: "/admin/cursos",
    icon: BookOpen,
    badge: null,
  },
  {
    title: "Gerenciar Vídeos",
    url: "/admin/videos",
    icon: Video,
    badge: null,
  },
  {
    title: "Gerenciar Categorias",
    url: "/admin/categorias",
    icon: Star,
    badge: null,
  },
  {
    title: "Gerenciar Instrutores",
    url: "/admin/instrutores",
    icon: UserCheck,
    badge: null,
  },
  {
    title: "Gerenciar Empresas",
    url: "/admin/empresas",
    icon: Building,
    badge: null,
  },
  {
    title: "Templates Certificados",
    url: "/admin/templates",
    icon: Award,
    badge: null,
  },
  {
    title: "Criar Curso",
    url: "/criar-curso",
    icon: Plus,
    badge: null,
  },
  {
    title: "Criar Vídeo",
    url: "/criar-video",
    icon: Video,
    badge: null,
  },
]

// Mapeamento de ícones para as categorias
const iconMap = {
  BookOpen, Code, Palette, TrendingUp, Users, Calculator, Camera, Heart, Globe,
  Monitor, Smartphone, Database, Cloud, Cpu, Settings, Briefcase, GraduationCap,
  Music, Video, Gamepad2, Wrench, Lightbulb, Star, Award, Target,
  BarChart3, PieChart, LineChart, Activity, Zap, Shield, Lock, Key
}

export function AppSidebar() {
  const sidebarRef = useRef(null)
  const { usuario, logout, isAdmin } = useAuth()
  const navigate = useNavigate()
  const [meusCursosCount, setMeusCursosCount] = useState(0)
  const [categorias, setCategorias] = useState([])
  const [loadingCategorias, setLoadingCategorias] = useState(true)

  const handleLogout = () => {
    logout()
    navigate('/auth')
  }

  const handleNavigation = (url) => {
    if (url !== '#') {
      navigate(url)
    }
  }

  // Buscar dados iniciais
  useEffect(() => {
    const carregarDados = async () => {
      if (usuario) {
        try {
          // Carregar meus cursos para usuários normais
          if (!isAdmin()) {
            const response = await cursosAPI.meusCursos()
            setMeusCursosCount(response.data?.length || 0)
          }

          // Carregar categorias para usuários normais
          if (!isAdmin()) {
            setLoadingCategorias(true)
            const categoriasResponse = await fetch('http://localhost:5000/api/categorias')
            const categoriasData = await categoriasResponse.json()
            setCategorias(categoriasData.categorias || [])
          }
        } catch (error) {
          console.error('Erro ao carregar dados:', error)
        } finally {
          setLoadingCategorias(false)
        }
      }
    }

    carregarDados()
  }, [usuario, isAdmin])

  // Escolhe os itens de navegação baseado no tipo de usuário
  const navigationItems = isAdmin() ? adminNavigationItems : getNormalNavigationItems(meusCursosCount)

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Verificar e animar entrada da sidebar
      const sidebarItems = sidebarRef.current?.querySelectorAll(".sidebar-item")
      if (sidebarItems && sidebarItems.length > 0) {
        gsap.fromTo(
          sidebarItems,
          { opacity: 0, x: -20 },
          {
            opacity: 1,
            x: 0,
            duration: 0.5,
            stagger: 0.05,
            ease: "power2.out",
            delay: 0.2,
          },
        )
      }
    }, sidebarRef)

    return () => ctx.revert()
  }, [])

  return (
    <div
      ref={sidebarRef}
      className="fixed left-0 top-0 w-64 h-screen bg-gradient-to-b from-slate-950/95 to-slate-900/95 backdrop-blur-xl border-r border-slate-800/50 flex flex-col z-50"
    >
      {/* Header */}
      <div className="sidebar-item p-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-gradient-to-r from-red-600 to-orange-500 rounded-lg flex items-center justify-center">
            <BookOpen className="w-4 h-4 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-white">EduFlix</h2>
            <p className="text-xs text-slate-400">
              {isAdmin() ? 'Painel Admin' : 'Plataforma de Cursos'}
            </p>
          </div>
        </div>
      </div>

      {/* Navigation - área rolável */}
      <div className="flex-1 px-4 overflow-y-auto">
        <div className="mb-6">
          <div className="sidebar-item text-slate-300 font-medium text-sm mb-3">
            {isAdmin() ? 'Administração' : 'Navegação'}
          </div>
          <nav className="space-y-1">
            {navigationItems.map((item) => (
              <button
                key={item.title}
                onClick={() => handleNavigation(item.url)}
                className="sidebar-item group flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-slate-800/50 transition-all duration-200 hover:scale-[1.02] w-full text-left"
              >
                <item.icon className="w-4 h-4 text-slate-400 group-hover:text-red-400 transition-colors" />
                <span className="text-slate-200 group-hover:text-white transition-colors flex-1">{item.title}</span>
                {item.badge && (
                  <Badge className="bg-red-600/20 text-red-400 border-red-600/30 text-xs">
                    {item.badge}
                  </Badge>
                )}
              </button>
            ))}
          </nav>
        </div>

        {/* Mostrar categorias apenas para usuários normais */}
        {!isAdmin() && (
          <>
            <Separator className="bg-slate-800/50 mb-6" />
            <div>
              <div className="sidebar-item text-slate-300 font-medium text-sm mb-3">Categorias</div>
              <nav className="space-y-1">
                {loadingCategorias ? (
                  <div className="flex items-center justify-center py-6">
                    <div className="w-5 h-5 border-2 border-slate-600 border-t-orange-400 rounded-full animate-spin"></div>
                  </div>
                ) : categorias.length > 0 ? (
                  categorias.map((categoria) => {
                    const IconComponent = iconMap[categoria.icone] || BookOpen
                    return (
                      <button
                        key={categoria.id}
                        onClick={() => handleNavigation(`/categoria/${categoria.id}`)}
                        className="sidebar-item group flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-slate-800/50 transition-all duration-200 hover:scale-[1.02] w-full text-left"
                      >
                        <IconComponent className="w-4 h-4 text-slate-400 group-hover:text-orange-400 transition-colors" />
                        <span className="text-slate-200 group-hover:text-white transition-colors">{categoria.nome}</span>
                      </button>
                    )
                  })
                ) : (
                  <div className="text-slate-400 text-sm text-center py-3">
                    Nenhuma categoria disponível
                  </div>
                )}
              </nav>
            </div>
          </>
        )}
      </div>

      {/* Footer - fixo na parte inferior */}
      <div className="sidebar-item p-4 border-t border-slate-800/50">
        <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-800/30 border border-slate-700/50 mb-2">
          <Avatar className="w-8 h-8">
            <AvatarFallback className="bg-gradient-to-r from-red-600 to-orange-500 text-white text-sm">
              {usuario?.nome?.charAt(0)?.toUpperCase() || 'U'}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white truncate flex items-center gap-1">
              {usuario?.nome || 'Usuário'}
              {isAdmin() && <Crown className="w-3 h-3 text-yellow-400" />}
            </p>
            <p className="text-xs text-slate-400 truncate">
              {isAdmin() ? 'Administrador' : 'Assinante'}
            </p>
          </div>
        </div>
        
        {/* Botão de logout */}
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-red-600/20 hover:border-red-600/30 transition-all duration-200 text-slate-400 hover:text-red-400 border border-transparent"
        >
          <LogOut className="w-4 h-4" />
          <span className="text-sm">Sair</span>
        </button>
      </div>
    </div>
  )
} 